import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL, fetchWithAuth } from '../../api';
import { Edit2, Check, X, Eye, EyeOff, User, Mail, Smartphone, CreditCard, Lock, Settings } from 'react-feather';

export default function ConfigProfile() {
  const { user, login } = useAuth();
  
  // States for Profile Fields
  const [editField, setEditField] = useState(null);
  const [fieldValue, setFieldValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // States for Password
  const [editPassword, setEditPassword] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (!user) return null;

  // Configuration for fields with Icons
  const fields = [
    { key: 'name', label: 'Nombre Completo', icon: User },
    { key: 'username', label: 'Nombre de Usuario', icon: User },
    { key: 'email', label: 'Email', icon: Mail },
    { key: 'dni', label: 'DNI', icon: CreditCard },
    { key: 'phone', label: 'Celular', icon: Smartphone },
  ];

  // --- HANDLERS ---

  const startEdit = (key) => {
    setEditField(key);
    setFieldValue(user[key] || '');
    setError('');
  };

  const cancelEdit = () => {
    setEditField(null);
    setFieldValue('');
    setError('');
  };

  const saveField = async (key) => {
    setLoading(true);
    setError('');
    try {
      const updatedUser = await fetchWithAuth(`${API_URL}/users/me`, {
        method: 'PATCH',
        body: JSON.stringify({ [key]: fieldValue }),
      });
      login(updatedUser); 
      setEditField(null);
      setFieldValue('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const savePassword = async () => {
    setPasswordLoading(true);
    setPasswordError('');
    try {
      await fetchWithAuth(`${API_URL}/users/me`, {
        method: 'PATCH',
        body: JSON.stringify({ password: passwordValue }),
      });
      setEditPassword(false);
      setPasswordValue('');
      setShowPassword(false);
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  // --- RENDER ---

  return (
    <div className="w-full space-y-8 animate-fade-in pb-20">
      
      {/* HEADER */}
      <div className="flex items-center gap-3 mb-2">
         <h2 className="text-2xl font-medium text-white flex items-center gap-2">
           <Settings className="text-white/80"/> Configuración
         </h2>
      </div>

      {/* 1. PERSONAL INFO SECTION */}
      <section className="bg-[#ffffff03] border border-white/5 rounded-3xl overflow-hidden">
        <div className="p-6 border-b border-white/5 bg-white/5">
            <h3 className="text-lg font-medium text-white">Información Personal</h3>
            <p className="text-sm text-gray-500">Actualiza tus datos de contacto e identificación.</p>
        </div>
        
        <div className="divide-y divide-white/5">
          {fields.map(({ key, label, icon: Icon }) => (
            <div key={key} className="p-6 flex flex-col md:flex-row md:items-center gap-4 hover:bg-[#ffffff02] transition-colors">
              
              {/* Label & Icon */}
              <div className="md:w-1/3 flex items-center gap-3 text-gray-400">
                 <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                    <Icon size={16} />
                 </div>
                 <span className="font-medium text-sm">{label}</span>
              </div>

              {/* Edit Mode vs View Mode */}
              <div className="flex-1">
                {editField === key ? (
                  <div className="flex items-center gap-2 animate-fade-in">
                    <input
                      className="flex-1 px-4 py-2 rounded-xl bg-black/50 border border-white/20 text-white focus:outline-none focus:border-purple-500 transition-all text-sm"
                      value={fieldValue}
                      onChange={e => setFieldValue(e.target.value)}
                      disabled={loading}
                      autoFocus
                    />
                    <button onClick={() => saveField(key)} disabled={loading} 
                        className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition">
                        <Check size={18} />
                    </button>
                    <button onClick={cancelEdit} 
                        className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition">
                        <X size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between group cursor-pointer" onClick={() => startEdit(key)}>
                    <span className="text-white text-base truncate pr-4">{user[key] || <span className="text-gray-600 italic">Sin definir</span>}</span>
                    <button className="text-gray-600 group-hover:text-purple-400 transition-colors opacity-0 group-hover:opacity-100">
                        <Edit2 size={16} />
                    </button>
                  </div>
                )}
                {error && editField === key && <p className="text-red-400 text-xs mt-2">{error}</p>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 2. SECURITY SECTION */}
      <section className="bg-[#ffffff03] border border-white/5 rounded-3xl overflow-hidden">
        <div className="p-6 border-b border-white/5 bg-white/5">
            <h3 className="text-lg font-medium text-white">Seguridad</h3>
            <p className="text-sm text-gray-500">Gestiona el acceso a tu cuenta.</p>
        </div>

        <div className="p-6 flex flex-col md:flex-row md:items-center gap-4">
            {/* Label */}
            <div className="md:w-1/3 flex items-center gap-3 text-gray-400">
                 <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                    <Lock size={16} />
                 </div>
                 <span className="font-medium text-sm">Contraseña</span>
            </div>

            {/* Content */}
            <div className="flex-1">
                {editPassword ? (
                  <div className="flex flex-col gap-3 animate-fade-in">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <input
                            className="w-full px-4 py-2 rounded-xl bg-black/50 border border-white/20 text-white focus:outline-none focus:border-purple-500 transition-all text-sm pr-10"
                            type={showPassword ? "text" : "password"}
                            value={passwordValue}
                            onChange={e => setPasswordValue(e.target.value)}
                            disabled={passwordLoading}
                            placeholder="Nueva contraseña"
                            autoFocus
                            />
                            <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                            >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                        <button onClick={savePassword} disabled={passwordLoading} 
                            className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition">
                            <Check size={18} />
                        </button>
                        <button onClick={() => { setEditPassword(false); setPasswordValue(''); setShowPassword(false); setPasswordError('')}} 
                            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition">
                            <X size={18} />
                        </button>
                    </div>
                    {passwordError && <p className="text-red-400 text-xs">{passwordError}</p>}
                    <p className="text-xs text-gray-500">Usa al menos 8 caracteres para mayor seguridad.</p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between group cursor-pointer" onClick={() => setEditPassword(true)}>
                    <span className="text-white/50 text-base tracking-widest">••••••••</span>
                    <button className="text-gray-600 group-hover:text-purple-400 transition-colors opacity-0 group-hover:opacity-100">
                        <Edit2 size={16} />
                    </button>
                  </div>
                )}
            </div>
        </div>
      </section>

    </div>
  );
}